import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, Building2, Calendar, MessageSquare, Clock, LogOut, Trash2, CheckCircle, Edit3, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [filter, setFilter] = useState('all');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchConsultations();
      fetchStats();
    }
  }, [filter, isAuthenticated]);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      // Verify token with backend
      await axios.get(`${API}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Authentication failed:', error);
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const url = filter === 'all' 
        ? `${API}/consultations`
        : `${API}/consultations?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setConsultations(response.data);
    } catch (error) {
      console.error('Error fetching consultations:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/consultations/count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const updateStatus = async (consultationId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/consultations/${consultationId}/status`,
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast({
        title: "Status Updated",
        description: `Consultation marked as ${newStatus}`,
      });

      // Refresh data
      fetchConsultations();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Update Failed",
        description: "Could not update status. Please try again.",
        variant: "destructive"
      });
    }
  };

  const addComment = async (consultationId) => {
    const comment = commentText[consultationId];
    if (!comment || !comment.trim()) {
      toast({
        title: "Empty Comment",
        description: "Please enter a comment before submitting.",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/consultations/${consultationId}/comment`,
        { comment },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast({
        title: "Comment Added",
        description: "Your comment has been saved successfully.",
      });

      // Clear comment text
      setCommentText(prev => ({ ...prev, [consultationId]: '' }));
      
      // Refresh data
      fetchConsultations();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Comment Failed",
        description: "Could not add comment. Please try again.",
        variant: "destructive"
      });
    }
  };

  const deleteConsultation = async (consultationId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete the consultation from ${customerName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/consultations/${consultationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast({
        title: "Consultation Deleted",
        description: "The consultation request has been permanently deleted.",
      });

      // Refresh data
      fetchConsultations();
      fetchStats();
    } catch (error) {
      console.error('Error deleting consultation:', error);
      toast({
        title: "Delete Failed",
        description: "Could not delete consultation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const toggleComments = (consultationId) => {
    setExpandedComments(prev => ({
      ...prev,
      [consultationId]: !prev[consultationId]
    }));
  };

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-12 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-light mb-4">
              Consultation <span className="font-medium text-brand-teal">Requests</span>
            </h1>
            <p className="text-muted-foreground">
              View and manage all consultation form submissions
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground border border-border hover:bg-secondary transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-secondary p-6 border-l-4 border-brand-teal">
            <p className="text-sm text-muted-foreground mb-1">Total Requests</p>
            <p className="text-3xl font-semibold">{stats.total}</p>
          </div>
          <div className="bg-secondary p-6 border-l-4 border-green-500">
            <p className="text-sm text-muted-foreground mb-1">New</p>
            <p className="text-3xl font-semibold text-green-600">{stats.new}</p>
          </div>
          <div className="bg-secondary p-6 border-l-4 border-yellow-500">
            <p className="text-sm text-muted-foreground mb-1">Contacted</p>
            <p className="text-3xl font-semibold text-yellow-600">{stats.contacted}</p>
          </div>
          <div className="bg-secondary p-6 border-l-4 border-blue-500">
            <p className="text-sm text-muted-foreground mb-1">Converted</p>
            <p className="text-3xl font-semibold text-blue-600">{stats.converted}</p>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-brand-teal text-white' 
                : 'bg-secondary text-foreground hover:bg-brand-teal/10'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('new')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'new' 
                ? 'bg-brand-teal text-white' 
                : 'bg-secondary text-foreground hover:bg-brand-teal/10'
            }`}
          >
            New
          </button>
          <button
            onClick={() => setFilter('contacted')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'contacted' 
                ? 'bg-brand-teal text-white' 
                : 'bg-secondary text-foreground hover:bg-brand-teal/10'
            }`}
          >
            Contacted
          </button>
          <button
            onClick={() => setFilter('converted')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === 'converted' 
                ? 'bg-brand-teal text-white' 
                : 'bg-secondary text-foreground hover:bg-brand-teal/10'
            }`}
          >
            Converted
          </button>
        </div>

        {/* Consultations List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading consultations...</p>
          </div>
        ) : consultations.length === 0 ? (
          <div className="text-center py-12 bg-secondary">
            <p className="text-muted-foreground">No consultation requests yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {consultations.map((consultation) => (
              <div
                key={consultation.id}
                className="bg-secondary border border-border p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-medium mb-1">{consultation.name}</h3>
                    <span
                      className={`inline-block px-3 py-1 text-xs font-semibold ${
                        consultation.status === 'new'
                          ? 'bg-green-100 text-green-700'
                          : consultation.status === 'contacted'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {consultation.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock size={16} />
                    {formatDate(consultation.createdAt)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail size={16} className="text-brand-teal" />
                    <a href={`mailto:${consultation.email}`} className="hover:text-brand-teal">
                      {consultation.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={16} className="text-brand-teal" />
                    <a href={`tel:${consultation.phone}`} className="hover:text-brand-teal">
                      {consultation.phone}
                    </a>
                  </div>
                  {consultation.company && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 size={16} className="text-brand-teal" />
                      {consultation.company}
                    </div>
                  )}
                  {consultation.timeline && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-brand-teal" />
                      {consultation.timeline}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Product Category</p>
                    <p className="text-sm">{consultation.productCategory}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Project Stage</p>
                    <p className="text-sm">{consultation.projectStage}</p>
                  </div>
                </div>

                <div className="bg-background p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <MessageSquare size={16} className="text-brand-teal mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-1">Message:</p>
                      <p className="text-sm text-muted-foreground">{consultation.message}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => updateStatus(consultation.id, 'new')}
                    disabled={consultation.status === 'new'}
                    className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                      consultation.status === 'new'
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : 'bg-secondary text-foreground hover:bg-green-100 hover:text-green-700'
                    }`}
                  >
                    <CheckCircle size={14} />
                    Mark as New
                  </button>
                  <button
                    onClick={() => updateStatus(consultation.id, 'contacted')}
                    disabled={consultation.status === 'contacted'}
                    className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                      consultation.status === 'contacted'
                        ? 'bg-yellow-100 text-yellow-700 cursor-not-allowed'
                        : 'bg-secondary text-foreground hover:bg-yellow-100 hover:text-yellow-700'
                    }`}
                  >
                    <CheckCircle size={14} />
                    Mark as Contacted
                  </button>
                  <button
                    onClick={() => updateStatus(consultation.id, 'converted')}
                    disabled={consultation.status === 'converted'}
                    className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                      consultation.status === 'converted'
                        ? 'bg-blue-100 text-blue-700 cursor-not-allowed'
                        : 'bg-secondary text-foreground hover:bg-blue-100 hover:text-blue-700'
                    }`}
                  >
                    <CheckCircle size={14} />
                    Mark as Converted
                  </button>
                  <button
                    onClick={() => deleteConsultation(consultation.id, consultation.name)}
                    className="px-3 py-1.5 text-xs font-medium flex items-center gap-1 bg-red-50 text-red-600 hover:bg-red-100 transition-colors ml-auto"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>

                {/* Comments Section */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => toggleComments(consultation.id)}
                      className="text-sm font-medium text-brand-teal hover:underline flex items-center gap-1"
                    >
                      <Edit3 size={14} />
                      {expandedComments[consultation.id] ? 'Hide Comments' : `Comments (${consultation.comments?.length || 0})`}
                    </button>
                  </div>

                  {expandedComments[consultation.id] && (
                    <div className="space-y-3">
                      {/* Existing Comments */}
                      {consultation.comments && consultation.comments.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {consultation.comments.map((comment, idx) => (
                            <div key={idx} className="bg-background p-3 border-l-2 border-brand-teal">
                              <p className="text-sm text-foreground mb-1">{comment.text}</p>
                              <p className="text-xs text-muted-foreground">
                                By {comment.addedBy} • {formatDate(comment.addedAt)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add New Comment */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={commentText[consultation.id] || ''}
                          onChange={(e) => setCommentText(prev => ({ ...prev, [consultation.id]: e.target.value }))}
                          placeholder="Add a comment or note..."
                          className="flex-1 px-3 py-2 text-sm border border-input bg-background focus:outline-none focus:ring-2 focus:ring-brand-teal"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addComment(consultation.id);
                            }
                          }}
                        />
                        <button
                          onClick={() => addComment(consultation.id)}
                          className="px-4 py-2 text-sm font-medium text-white bg-brand-teal hover:opacity-90 flex items-center gap-1"
                        >
                          <Send size={14} />
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
